/**
 * Map the GRM GetTeamCalendar response into `FMForecastRow[]` for `FMForecastView`.
 * Behavior-parity port of `fmdashboard.controller.js` `FetchManagerCalendar` / the ScheduleIntervals
 * split (~:315-333) / `populateDataForCalendarEvent` (~:482-561) / `eventDetails` (~:553-561) and the
 * directive geometry in `fmForecastLayout` (`fetchLines`). Pure/side-effect-free so it is unit-tested
 * against fixtures and reused without the FXP shell.
 */
import {
  GetTeamCalendarResponse,
  GrmCalendarItem,
  GrmEventType,
  TeamCalendarResource,
} from '../../../contracts/FMGrmContracts';
import { ForecastEventInput, ForecastRowInput } from '../../../contracts/FMForecastContracts';
import { getNumberOfDaysInMonth, layoutRows } from './fmForecastLayout';
import { FMForecastRow } from '../FMForecastView/FMForecastView';

const DEFAULT_COLOR = '#29c7ee';
const SYSTEM_COLOR = '#2ecc71';
/** Colors that force black text regardless of luminance (parity: the 5 hard overrides). */
const FORCE_BLACK_TEXT = new Set(
  ['#ff00ff', '#808000', '#d2691e', '#708090', '#ff0000'].map((c) => c.toLowerCase())
);

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/** Parity of `getWeekends`: weekday-name strings → JS day indices (0-6). */
export function getWeekends(weekends?: string[]): number[] {
  if (!weekends) {
    return [];
  }
  return weekends
    .map((w) => DAY_NAME_TO_INDEX[(w || '').toLowerCase()])
    .filter((n): n is number => n !== undefined);
}

/** Day-of-month numbers covered by a resource's public holidays (parity: the holiday loop). */
export function getPublicHolidayDays(resource: TeamCalendarResource): number[] {
  const days: number[] = [];
  for (const h of resource.PublicHolidays ?? []) {
    const start = new Date(h.StartDateTime);
    const end = new Date(h.EndDateTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      continue;
    }
    const s = start.getDate();
    const e = end.getDate();
    if (s === e) {
      days.push(s);
    } else {
      for (let j = s; j <= e; j++) {
        days.push(j);
      }
    }
  }
  return days;
}

/**
 * Relative luminance dark check (replaces tinycolor.isDark from the source).
 * Returns true when the color is dark → white text; false → black text.
 */
export function isColorDark(hex?: string): boolean {
  if (!hex) {
    return true;
  }
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) {
    return true;
  }
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  // Perceived brightness (YIQ); < 128 => dark.
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

/** Look up an event type's color + IsApplicableToRCs by EventTypeId (parity: `eventDetails`). */
function eventDetails(
  item: GrmCalendarItem,
  eventTypes: GrmEventType[]
): { color?: string; isApplicableToRCs?: boolean } {
  const match = eventTypes.find((et) => et.EventTypeId === item.EventTypeId);
  return match ? { color: match.EventColor, isApplicableToRCs: match.IsApplicableToRCs } : {};
}

const isTrue = (v?: string | boolean): boolean =>
  typeof v === 'boolean' ? v : String(v ?? '').toLowerCase() === 'true';

/** 1-based day-of-month for an event start, clamped into the visible month (parity: getStartDateColumnIdx). */
function startDateIndexFor(dateStr: string, monthStart: Date): number {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    return 0;
  }
  return d <= monthStart ? monthStart.getDate() : d.getDate();
}

/**
 * Inclusive day-count for a bar, clamped to the month (parity: getDateDiffInDays — anchors
 * start 08:00 / end 20:00, +1 day, rounded).
 */
function durationFor(startStr: string, endStr: string, monthStart: Date, monthEnd: Date): number {
  let f = new Date(startStr);
  let s = new Date(endStr);
  if (isNaN(f.getTime()) || isNaN(s.getTime())) {
    return 0;
  }
  if (f <= monthStart) {
    f = new Date(monthStart);
  }
  if (s >= monthEnd) {
    s = new Date(monthEnd);
  }
  f.setHours(8, 0, 0, 0);
  s.setHours(20, 0, 0, 0);
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(Math.abs((f.getTime() - s.getTime() + 1) / ONE_DAY));
  return diffDays + 1;
}

/** Build one bar (ForecastEventInput) for an interval of a calendar item (parity: populateDataForCalendarEvent). */
function buildBar(
  item: GrmCalendarItem,
  schStart: string,
  schEnd: string,
  monthStart: Date,
  monthEnd: Date,
  eventTypes: GrmEventType[]
): ForecastEventInput {
  let colorName = DEFAULT_COLOR;
  let eventtextcolor = '#ffffff';
  if (item.EventTypeId === -1) {
    colorName = SYSTEM_COLOR;
  } else {
    const cfg = eventDetails(item, eventTypes);
    if (cfg.color) {
      colorName = cfg.color;
      eventtextcolor = isColorDark(cfg.color) ? '#FFFFFF' : '#000000';
      if (FORCE_BLACK_TEXT.has(cfg.color.toLowerCase())) {
        eventtextcolor = '#000000';
      }
    }
  }

  return {
    startDateIndex: startDateIndexFor(schStart, monthStart),
    duration: durationFor(schStart, schEnd, monthStart, monthEnd),
    feature: item.Name,
    startDate: schStart,
    endDate: schEnd,
    status: item.Status,
    colorName,
    eventtextcolor,
    demandSourceId:
      item.AdditionalProperties?.DemandSourceId !== undefined
        ? String(item.AdditionalProperties.DemandSourceId)
        : undefined,
    assignmentType: item.AssignmentType,
  };
}

/** Split one calendar item into bars (parity: the ScheduleIntervals loop + the IsContiguous/BookingType skip). */
export function itemToBars(
  item: GrmCalendarItem,
  monthStart: Date,
  monthEnd: Date,
  eventTypes: GrmEventType[]
): ForecastEventInput[] {
  const intervals = item.ScheduleIntervals;
  if (intervals && intervals.length > 0) {
    return intervals.map((iv) => buildBar(item, iv.StartDate, iv.EndDate, monthStart, monthEnd, eventTypes));
  }
  // Non-contiguous project booking with no intervals → skip (parity: Bug 2512936).
  if (!isTrue(item.AdditionalProperties?.IsContiguous) && item.BookingType === 1) {
    return [];
  }
  return [buildBar(item, item.StartDate, item.EndDate, monthStart, monthEnd, eventTypes)];
}

const utilText = (v?: number | string): string | number =>
  v === undefined || v === null || v === '' ? '--' : v;

export interface MapTeamCalendarOptions {
  /** Event types master data (for bar colors). */
  eventTypes?: GrmEventType[];
  /** First day of the visible month (defaults to now's month start). */
  monthStart?: Date;
}

/**
 * Map a GetTeamCalendar response into laid-out `FMForecastRow[]` (bars level-packed via `layoutRows`).
 * Profile columns (%/skills/endorsement) come from OneProfile and are merged separately by the page.
 */
export function mapTeamCalendarToRows(
  response: GetTeamCalendarResponse | undefined,
  options: MapTeamCalendarOptions = {}
): FMForecastRow[] {
  const items = response?.TeamCalendarItems;
  if (!items) {
    return [];
  }
  const now = new Date();
  const monthStart = options.monthStart ?? new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const numberOfDays = getNumberOfDaysInMonth(monthStart);
  const eventTypes = options.eventTypes ?? [];

  const inputs: ForecastRowInput[] = Object.keys(items).map((alias) => {
    const resource = items[alias];
    const events: ForecastEventInput[] = [];
    for (const item of resource.CalendarItems ?? []) {
      events.push(...itemToBars(item, monthStart, monthEnd, eventTypes));
    }
    return {
      rowId: alias,
      name: `${resource.FullName} (${alias})`,
      timeZone: resource.TimeZone,
      utility1: utilText(resource.Utilization?.NinetyDayAvgUtilization),
      utility2: utilText(resource.Utilization?.YtdUtilization),
      weekends: getWeekends(resource.Weekends),
      publicHolidays: getPublicHolidayDays(resource),
      events,
    };
  });

  // layoutRows returns LaidOutRow[] (rowId/name/utility/weekends/holidays/contractHours/lines/height)
  // which is assignable to FMForecastRow (all extra fields optional).
  return layoutRows(inputs, numberOfDays) as FMForecastRow[];
}
